import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../axios";
import API_BASE_URL from "../../config";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Box,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

export default function EditProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const companyId = parseInt(localStorage.getItem("companyId") || "0", 10);
  const branchIdFromLogin = parseInt(localStorage.getItem("branchId") || "0", 10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatTypes, setVatTypes] = useState([]);
  const [unitDefs, setUnitDefs] = useState([]);

  const [formData, setFormData] = useState({
    arabicName: "",
    englishName: "",
    description: "",
    imageUrl: "",
    code: "",
    itemId: 0,
    taxId: 0,
    companyId,
    branchId: branchIdFromLogin,
    categoryId: 0,
    isService: false,
    showInPOS: true,
    units: [],
  });

  useEffect(() => {
    async function loadData() {
      try {
        const [bRes, cRes, vRes, uRes, pRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Branch/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/Category/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/VatTypes?companyId=${companyId}`),
          axios.get(`${API_BASE_URL}/Unit/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/Product/by-company/${companyId}`),
        ]);

        setBranches(bRes.data);
        setCategories(cRes.data);
        setVatTypes(vRes.data);
        setUnitDefs(uRes.data);

        const numericId = parseInt(id || "0", 10);
        const product = Array.isArray(pRes.data)
          ? pRes.data.find((x) => x.id === numericId)
          : null;

        if (!product) {
          setError("لم يتم العثور على المنتج المطلوب");
          return;
        }

        const baseBranchId =
          branchIdFromLogin > 0
            ? branchIdFromLogin
            : bRes.data.length > 0
            ? bRes.data[0].id
            : 0;

        const mappedUnits = (product.units || []).map((u) => ({
          unitId: u.unitId,
          factor: u.factor || 1,
          salPrice: typeof u.salprice === "number" ? u.salprice : 0,
          purchPrice: typeof u.purchprice === "number" ? u.purchprice : 0,
          barcode: u.barcode || "",
          isBaseUnit: !!u.isBaseUnit,
        }));

        setFormData({
          arabicName: product.arabicName || "",
          englishName: product.englishName || "",
          description: product.description || "",
          imageUrl: "",
          code: product.code || "",
          itemId: product.itemId || 0,
          taxId: product.taxId || (vRes.data.length > 0 ? vRes.data[0].id : 0),
          companyId,
          branchId: baseBranchId,
          categoryId: product.categoryId || (cRes.data.length > 0 ? cRes.data[0].id : 0),
          isService: !!product.isService,
          showInPOS: !!product.showInPOS,
          units: mappedUnits.length > 0
            ? mappedUnits
            : [
                {
                  unitId: uRes.data.length > 0 ? uRes.data[0].id : 0,
                  factor: 1,
                  salPrice: 0,
                  purchPrice: 0,
                  barcode: "",
                  isBaseUnit: true,
                },
              ],
        });
      } catch (e) {
        setError("فشل في تحميل بيانات المنتج");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id, companyId, branchIdFromLogin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addUnit = () => {
    setFormData((prev) => ({
      ...prev,
      units: [
        ...prev.units,
        {
          unitId: unitDefs.length > 0 ? unitDefs[0].id : 0,
          factor: 1,
          salPrice: 0,
          purchPrice: 0,
          barcode: "",
          isBaseUnit: prev.units.length === 0,
        },
      ],
    }));
  };

  const removeUnit = (idxToRemove) => {
    setFormData((prev) => ({
      ...prev,
      units: prev.units.filter((_, idx) => idx !== idxToRemove),
    }));
  };

  const handleUnitChange = (idx, field, value) => {
    setFormData((prev) => {
      const units = [...prev.units];
      if (field === "isBaseUnit" && value === true) {
        units.forEach((unit, i) => {
          if (i !== idx) unit.isBaseUnit = false;
        });
      }
      units[idx][field] = value;
      return { ...prev, units };
    });
  };

  const handleBarcodeChange = (uIdx, value) => {
    setFormData((prev) => {
      const units = [...prev.units];
      units[uIdx].barcode = value;
      return { ...prev, units };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_BASE_URL}/Product/${companyId}/${formData.itemId}`,
        formData
      );
      alert("✅ تم تعديل المنتج بنجاح");
      navigate("/productList");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.innerError ||
        err.message;
      alert("❌ حدث خطأ أثناء التعديل: " + errorMessage);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4 }} dir="rtl">
      <Card>
        <CardHeader
          title={
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              تعديل المنتج
            </Typography>
          }
          sx={{ backgroundColor: "#1976d2", color: "white" }}
        />
        <CardContent>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: "#3f51b5" }}>
              معلومات المنتج الأساسية
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="الاسم العربي" name="arabicName"
                  value={formData.arabicName} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="الاسم الإنجليزي" name="englishName"
                  value={formData.englishName} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="رقم المنتج (ItemId)" name="itemId"
                  value={formData.itemId} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="كود المنتج" name="code"
                  value={formData.code} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>المجموعة</InputLabel>
                  <Select name="categoryId" value={formData.categoryId} onChange={handleChange}>
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.nameAr}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>الفرع</InputLabel>
                  <Select name="branchId" value={formData.branchId} onChange={handleChange}>
                    {branches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>نوع الضريبة</InputLabel>
                  <Select name="taxId" value={formData.taxId} onChange={handleChange}>
                    {vatTypes.map((v) => (
                      <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={
                  <Switch checked={formData.isService}
                    onChange={(e) => handleChange({ target: { name: "isService", checked: e.target.checked, type: "checkbox" } })}
                  />
                } label="خدمة" />
                <FormControlLabel control={
                  <Switch checked={formData.showInPOS}
                    onChange={(e) => handleChange({ target: { name: "showInPOS", checked: e.target.checked, type: "checkbox" } })}
                  />
                } label="إظهار في نقطة البيع" />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ color: "#3f51b5" }}>الوحدات والباركود</Typography>
              <Button variant="contained" onClick={addUnit}>إضافة وحدة</Button>
            </Box>

            {formData.units.map((unit, idx) => (
              <Card key={idx} variant="outlined" sx={{ mb: 3, p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    الوحدة #{idx + 1}
                  </Typography>
                  <IconButton color="error" onClick={() => removeUnit(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>الوحدة</InputLabel>
                      <Select value={unit.unitId}
                        onChange={(e) => handleUnitChange(idx, "unitId", e.target.value)}>
                        {unitDefs.map((u) => (
                          <MenuItem key={u.id} value={u.id}>{u.nameAr}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth label="سعر الشراء" type="number"
                      value={unit.purchPrice}
                      onChange={(e) => handleUnitChange(idx, "purchPrice", Number(e.target.value))} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField fullWidth label="سعر البيع" type="number"
                      value={unit.salPrice}
                      onChange={(e) => handleUnitChange(idx, "salPrice", Number(e.target.value))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="معامل التحويل" type="number"
                      value={unit.factor}
                      onChange={(e) => handleUnitChange(idx, "factor", Number(e.target.value))} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel control={
                      <Switch checked={unit.isBaseUnit}
                        onChange={(e) => handleUnitChange(idx, "isBaseUnit", e.target.checked)} />
                    } label="وحدة أساسية" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="الباركود"
                      value={unit.barcode || ""}
                      onChange={(e) => handleBarcodeChange(idx, e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Divider sx={{ my: 4 }} />

            <Box textAlign="right">
              <Button type="submit" variant="contained" color="primary" size="large">
                💾 حفظ التعديلات
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => navigate("/productList")} sx={{ mr: 2 }}>
                إلغاء
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
